import React, { useEffect, useRef, useState } from "react";
import { LayoutGrid, FileText, CreditCard, BarChart3, Users, Settings } from "lucide-react";

const A = "/assets/login";
const ARIAONE = "https://www.ariaone.ai/";
const LINKEDIN = "https://www.linkedin.com/company/ariaoneai";
const FACEBOOK = "https://www.facebook.com/ariaoneai";

const SLIDES = [
  { eyebrow: "LENS", l1: "Upload what you have.", l2: "Get a full chart.", sub: "Last visit notes, lab results, hospital records, from any vendor. Lens reads them and builds the chart. No setup, no import project.", photo: "login-photo-lens-frontdesk", pos: "14% 32%", label: "Show Lens" },
  { eyebrow: "SCRIBE", l1: "More than a scribe.", l2: "A note that knows the chart.", sub: "Scribe listens to the visit and drafts the SOAP note with active meds, orders and history already in it, because Lens gave it the chart first.", photo: "login-photo-scribe-doctor", pos: "74% 30%", label: "Show Scribe" },
  { eyebrow: "PAYMENTS", l1: "Paper EOB in.", l2: "Payment posted.", sub: "Upload or snap a paper EOB. AriaOne turns it into an ERA and posts it line by line, ready for review.", photo: null, label: "Show Payments" },
  { eyebrow: "CLAIM TRACKER", l1: "Sent the claim?", l2: "Track it to paid.", sub: "Every claim after it goes out: sending, at the payer, paid or rejected. A rejection comes back with the reason and the field to fix.", photo: null, label: "Show Claim Tracker" },
];

const FileIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2Z" />
  </svg>
);

function SlideLens() {
  return (
    <div className="bp-screen">
      <div className="bp-chips">
        {["Last visit note.pdf", "CBC + CMP results.pdf", "Discharge summary.jpg"].map((n) => (
          <span className="bp-chip" key={n}><FileIcon />{n}</span>
        ))}
      </div>
      <div className="bp-card bp-chart">
        <div className="bp-chart-hd">
          <div>
            <div className="bp-chart-name">Maria Alvarez</div>
            <div className="bp-chart-meta">58 · F · Westside Family Medicine</div>
          </div>
          <span className="bp-tag">Built by Lens</span>
        </div>
        <div className="bp-boxes">
          <div className="bp-box"><div className="cap">Active meds</div><div className="ln">Metformin 500 mg BID · Lisinopril 10 mg daily · Atorvastatin 20 mg</div></div>
          <div className="bp-box"><div className="cap">Problems</div><div className="ln">Type 2 diabetes · Hypertension · Hyperlipidemia</div></div>
          <div className="bp-box"><div className="cap">Recent labs</div><div className="ln">A1c 7.4% · Oct 12 · LDL 96 · Oct 12 · eGFR 78 · Oct 12</div></div>
        </div>
        <div className="bp-chart-foot">3 documents read · 14 chart items added · ready for the next visit</div>
      </div>
    </div>
  );
}

function SlideScribe() {
  return (
    <div className="bp-screen">
      <div className="bp-card bp-note">
        <div className="bp-note-hd">
          <span className="bp-note-live"><span className="dot" />Listening · 04:12</span>
          <span className="bp-note-meta">Maria Alvarez · Diabetes follow-up</span>
        </div>
        <div className="bp-soap">
          <span className="k">S</span><span>Fasting sugars down since last visit. No hypoglycemia. Walking 3× a week.</span>
          <span className="k">O</span><span>BP 128/78. A1c 7.4% (Oct 12)<span className="bp-pillin">from chart</span></span>
          <span className="k">A</span><span style={{ color: "#6C6C69" }}>Left for the provider to write.</span>
          <span className="k">P</span><span>Continue metformin 500 mg BID<span className="bp-pillin">active med</span> · Repeat A1c in 3 months<span className="bp-pillin">order drafted</span></span>
        </div>
        <div className="bp-note-foot">Meds, orders and history are already in the note. Nothing is added without your accept.</div>
      </div>
    </div>
  );
}

const WinNav = () => (
  <div className="bp-win-nav">
    <img src={`${A}/ariaone-emblem-black.png`} alt="" />
    {[LayoutGrid, FileText, CreditCard, BarChart3, Users, Settings].map((Ic, i) => (
      <Ic key={i} className="ic" size={15} strokeWidth={1.7} />
    ))}
  </div>
);

const RevqChip = () => <span className="bp-chip-sm">revQ</span>;

function SlidePayments() {
  const rows = [
    ["Ana Morales", "Claim 7714-01 · Aetna", "10/01", "$192.00", "$148.20"],
    ["Daniel Kim", "Claim 7714-02 · Aetna", "10/02", "$280.00", "$221.00"],
    ["Grace Liu", "Claim 7714-03 · Aetna", "10/03", "$112.00", "$87.40"],
    ["Robert Hayes", "Claim 7714-04 · Aetna", "10/06", "$1,420.00", "$1,201.00"],
    ["Sofia Rossi", "Claim 7714-05 · Aetna", "10/08", "$240.00", "$185.00"],
  ];
  const grid = { gridTemplateColumns: "1.5fr .8fr .8fr .9fr 1fr" };
  return (
    <div className="bp-screen">
      <div className="bp-win">
        <WinNav />
        <div className="bp-win-main">
          <div className="bp-win-top">
            <div>
              <div className="bp-wtitle">Payment Posting<RevqChip /></div>
              <div className="bp-wsub">Last ERA received Oct 21, 7:30 AM</div>
            </div>
            <button className="bp-inkbtn">Upload EOBs</button>
          </div>
          <div className="bp-tabs">
            {["Posting", "Deposits", "Line Audit", "ERA Builder"].map((t) => (
              <span key={t} className={`bp-tab ${t === "ERA Builder" ? "on" : ""}`}>{t}</span>
            ))}
          </div>
          <div className="bp-stats">
            <div className="bp-stat"><div className="cap">Check #004418</div><div className="val">$1,842.60</div><div className="sub">Aetna · paid Oct 14</div></div>
            <div className="bp-stat"><div className="cap">Claims matched</div><div className="val">5 of 5</div><div className="sub">11 service lines</div></div>
            <div className="bp-stat"><div className="cap">Adjusted</div><div className="val">$401.40</div><div className="sub">CO-45 contractual</div></div>
            <div className="bp-stat"><div className="cap">Patient owes</div><div className="val">$65.00</div><div className="sub">PR-2 coinsurance</div></div>
          </div>
          <div className="bp-filerow">
            <div><div className="bp-filename">eob_aetna_1014.pdf · 4 pages</div><div className="bp-filesub">Read by Lens · ERA built from the paper EOB</div></div>
            <div className="bp-filebtns"><button className="bp-obtn">835</button><button className="bp-inkbtn">Post payments</button></div>
          </div>
          <div className="bp-table">
            <div className="bp-thead" style={grid}><span>Patient · Claim</span><span>DOS</span><span>Billed</span><span>Paid</span><span>Status</span></div>
            {rows.map((r) => (
              <div className="bp-trow" style={grid} key={r[0]}>
                <span><b>{r[0]}</b><br /><span className="bp-rowsub">{r[1]}</span></span>
                <span>{r[2]}</span><span>{r[3]}</span><span>{r[4]}</span>
                <span><span className="bp-greenpill">Matched</span></span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SlideTracker() {
  const segs = [["All 48", null, true], ["Incomplete 2", "#FF9500"], ["Sending 6", null], ["At payer 17", null], ["Processed 20", "#34C759"], ["Rejected 3", "#FF3B30"]];
  const rows = [
    { p: "Maria Garcia", psub: "MRN 20419 · Oct 22", claim: "A22184 · B03311", payer: "Aetna", dot: "#9B9B97", st: "At payer", stc: "#141412", stsub: "Payer acknowledged Oct 27", charge: "$185.00" },
    { p: "Keisha Brown", psub: "MRN 29904 · Oct 20", claim: "A22171 · B03306", payer: "Blue Cross", dot: "#FF3B30", st: "Rejected", stc: "#C4302A", stsub: "1 rejection · Subscriber ID not on file", charge: "$96.00" },
    { p: "James Okafor", psub: "MRN 20472 · Oct 22", claim: "A22179 · B03309", payer: "UnitedHealthcare", dot: "#34C759", st: "Processed", stc: "#141412", stsub: "ERA received Oct 28 · $112.40 paid", charge: "$142.00" },
    { p: "Tom Becker", psub: "MRN 20488 · Oct 21", claim: "A22166 · B03304", payer: "Medicare", dot: "#9B9B97", st: "Sending", stc: "#141412", stsub: "Transmitted Oct 29, 9:12 AM", charge: "$240.00" },
    { p: "Sofia Rossi", psub: "MRN 20503 · Oct 19", claim: "A22160 · B03301", payer: "Aetna", dot: "#34C759", st: "Processed", stc: "#141412", stsub: "ERA received Oct 27 · $151.20 paid", charge: "$168.00" },
  ];
  const grid = { gridTemplateColumns: "1.3fr 1.1fr 1fr 1.6fr .7fr" };
  return (
    <div className="bp-screen">
      <div className="bp-win">
        <WinNav />
        <div className="bp-win-main">
          <div className="bp-win-top">
            <div>
              <div className="bp-weyebrow">CLAIMS</div>
              <div className="bp-wtitle">Claim Tracker<RevqChip /></div>
              <div className="bp-wsub">Every claim after it's sent, from batch to ERA.</div>
            </div>
            <div className="bp-filebtns"><span className="bp-textbtn">Rejection analyzer</span><span className="bp-textbtn">Print claims</span><button className="bp-obtn">Refresh</button></div>
          </div>
          <div className="bp-tabs">
            {["Primary", "Secondary", "Paper", "Self-pay"].map((t) => (
              <span key={t} className={`bp-tab ${t === "Primary" ? "on" : ""}`}>{t}</span>
            ))}
          </div>
          <div className="bp-seg">
            {segs.map(([t, dot, on]) => (
              <span key={t} className={`bp-segitem ${on ? "on" : ""}`}>{dot && <span className="bp-segdot" style={{ background: dot }} />}{t}</span>
            ))}
          </div>
          <div className="bp-needs"><b>Needs you</b><span className="bp-needsitems">3 rejected need a fix · 1 invalid eligibility · 2 waiting 7+ days on payer</span></div>
          <div className="bp-table">
            <div className="bp-thead" style={grid}><span>Patient</span><span>Claim · Batch</span><span>Payer</span><span>Status</span><span>Charge</span></div>
            {rows.map((r) => (
              <div className="bp-trow" style={grid} key={r.p}>
                <span><b>{r.p}</b><br /><span className="bp-rowsub">{r.psub}</span></span>
                <span>{r.claim}</span>
                <span>{r.payer}</span>
                <span><span className="bp-stwrap"><span className="bp-stdot" style={{ background: r.dot }} /><b style={{ color: r.stc }}>{r.st}</b></span><br /><span className="bp-rowsub">{r.stsub}</span></span>
                <span>{r.charge}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const SCREENS = [SlideLens, SlideScribe, SlidePayments, SlideTracker];

export default function BrandPanel({ formColRef }) {
  const [cur, setCur] = useState(0);
  const [paused, setPaused] = useState(false);
  const hoverRef = useRef(false);
  const focusRef = useRef(false);
  const reduceRef = useRef(false);

  useEffect(() => {
    reduceRef.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const fc = formColRef?.current;
    const sync = () => setPaused(hoverRef.current || focusRef.current);
    const onIn = () => { focusRef.current = true; sync(); };
    const onOut = () => { focusRef.current = false; sync(); };
    fc?.addEventListener("focusin", onIn);
    fc?.addEventListener("focusout", onOut);
    const id = setInterval(() => {
      if (reduceRef.current || hoverRef.current || focusRef.current) return;
      setCur((c) => (c + 1) % SLIDES.length);
    }, 6000);
    return () => {
      clearInterval(id);
      fc?.removeEventListener("focusin", onIn);
      fc?.removeEventListener("focusout", onOut);
    };
  }, [formColRef]);

  const enter = () => { hoverRef.current = true; setPaused(true); };
  const leave = () => { hoverRef.current = false; setPaused(hoverRef.current || focusRef.current); };

  return (
    <aside className="lg-brand" onMouseEnter={enter} onMouseLeave={leave} data-testid="login-brand-panel">
      <div className="bp-photos" aria-hidden="true">
        {SLIDES.map((s, i) => s.photo ? (
          <picture key={i}>
            <source srcSet={`${A}/${s.photo}.webp`} type="image/webp" />
            <img className={`bp-photo ${cur === i ? "on" : ""}`} src={`${A}/${s.photo}.jpg`} alt="" style={{ objectPosition: s.pos }} />
          </picture>
        ) : null)}
        <div className={`bp-scrim ${cur < 2 ? "on" : ""}`} />
      </div>

      <div className="bp-top">
        <span className="bp-pill"><span className="dot" />Every feature free for 30 days</span>
        <div className="bp-topright">
          <a className="bp-link" href={ARIAONE} target="_blank" rel="noreferrer">ariaone.ai ↗</a>
          <a className="bp-social" href={LINKEDIN} target="_blank" rel="noreferrer" aria-label="AriaOne on LinkedIn"><img src={`${A}/icon-linkedin.svg`} alt="" /></a>
          <a className="bp-social" href={FACEBOOK} target="_blank" rel="noreferrer" aria-label="AriaOne on Facebook"><img src={`${A}/icon-facebook.svg`} alt="" /></a>
        </div>
      </div>

      <div className="bp-body">
        {SLIDES.map((s, i) => {
          const Screen = SCREENS[i];
          return (
            <div className={`bp-slide ${cur === i ? "on" : ""}`} key={i} aria-hidden={cur !== i}>
              <div className="bp-head" aria-live={paused ? "polite" : "off"}>
                <div className="bp-eyebrow">{s.eyebrow}</div>
                <div className="bp-h1">{s.l1}<br /><span className="grad">{s.l2}</span></div>
                <div className="bp-sub">{s.sub}</div>
              </div>
              <Screen />
            </div>
          );
        })}
      </div>

      <div className="bp-bottom">
        <div className="bp-dots">
          {SLIDES.map((s, i) => (
            <button key={i} className={`bp-dot ${cur === i ? "on" : ""}`} aria-label={s.label} onClick={() => setCur(i)} data-testid={`login-slide-dot-${i}`} />
          ))}
        </div>
        <a className="bp-link" href={ARIAONE} target="_blank" rel="noreferrer">See everything AriaOne does →</a>
      </div>
    </aside>
  );
}
